import { SugarMoneyApiEnvironmentsEnum } from '../constants';
import { Wretch } from 'wretch';
import { ApiResponse } from 'types/api';
import { ApiRequestParams } from 'types/api-internal';
type WretchWithAddons = Wretch & {
  query: (params: Record<string, unknown>) => Wretch;
  formData: (data: FormData) => Wretch;
};

async function blobToBuffer(blob: Blob): Promise<Buffer> {
  const arrayBuffer = await blob.arrayBuffer();
  return Buffer.from(arrayBuffer);
}

export const makeApiRequest = async <T>({
  endpoint,
  data,
  options,
  searchParams,
  wretchInstance,
  token,
  environment,
}: ApiRequestParams): Promise<T> => {
  let request = wretchInstance.url(endpoint) as WretchWithAddons;

  if (token) {
    const cookieName = environment === SugarMoneyApiEnvironmentsEnum.Local ? '' : '__Secure-';
    request = request.headers({
      Cookie: `${cookieName}next-auth.session-token=${token}`,
    }) as WretchWithAddons;
  }

  if (searchParams) {
    request = request.query(
      Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) => value !== undefined && value !== null),
      ),
    ) as WretchWithAddons;
  }

  if (data) {
    if (options.isFormData) {
      const boundary = '----WebKitFormBoundary' + Math.random().toString(36).slice(2);

      const formParts: (string | Buffer)[] = [];

      if (data instanceof FormData) {
        for (const [key, value] of data.entries()) {
          if (value instanceof Blob) {
            const filename = (value as any).name || 'blob';
            formParts.push(
              `--${boundary}\r\n` +
                `Content-Disposition: form-data; name="${key}"; filename="${filename}"\r\n` +
                `Content-Type: ${value.type || 'application/octet-stream'}\r\n\r\n`,
            );
            formParts.push(await blobToBuffer(value));
            formParts.push('\r\n');
          } else {
            formParts.push(
              `--${boundary}\r\n` +
                `Content-Disposition: form-data; name="${key}"\r\n\r\n` +
                `${value}\r\n`,
            );
          }
        }
      } else {
        for (const [key, value] of Object.entries(data as Record<string, any>)) {
          if (value instanceof Blob) {
            const filename = (value as any).name || 'blob';
            formParts.push(
              `--${boundary}\r\n` +
                `Content-Disposition: form-data; name="${key}"; filename="${filename}"\r\n` +
                `Content-Type: ${value.type || 'application/octet-stream'}\r\n\r\n`,
            );
            formParts.push(await blobToBuffer(value));
            formParts.push('\r\n');
          } else {
            formParts.push(
              `--${boundary}\r\n` +
                `Content-Disposition: form-data; name="${key}"\r\n\r\n` +
                `${typeof value === 'string' ? value : JSON.stringify(value)}\r\n`,
            );
          }
        }
      }

      formParts.push(`--${boundary}--\r\n`);

      const body = Buffer.concat(
        formParts.map((part) => (typeof part === 'string' ? Buffer.from(part) : part)),
      );

      request = request
        .headers({
          'Content-Type': `multipart/form-data; boundary=${boundary}`,
          'Content-Length': body.length.toString(),
        })
        .body(body) as WretchWithAddons;
    } else {
      request = request.json(data as object) as WretchWithAddons;
    }
  }

  const response = await request[options.method]().json<ApiResponse<T>>();

  return response.data;
};
